import React from 'react';
import { Box, Typography } from '@mui/material';
import { ProjectLandLotMap } from '../map';
import type { TopViewingSitePlan } from '../../services/api';

const THUMBNAIL_HEIGHT = 200;

function hasMapCoordinates(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): boolean {
  return (
    latitude != null &&
    longitude != null &&
    Number.isFinite(Number(latitude)) &&
    Number.isFinite(Number(longitude))
  );
}

interface SitePlanCardThumbnailProps {
  sitePlan: TopViewingSitePlan;
  /** Upcoming cards inset the thumbnail inside the animated border. */
  upcoming?: boolean;
}

const SitePlanCardThumbnail: React.FC<SitePlanCardThumbnailProps> = ({
  sitePlan,
  upcoming = false,
}) => {
  const frameSx = {
    width: upcoming ? 'calc(100% - 8px)' : '100%',
    height: THUMBNAIL_HEIGHT,
    ...(upcoming && {
      m: '4px',
      borderRadius: '4px',
    }),
  };

  if (sitePlan.thumbnailUrl) {
    return (
      <Box
        component="img"
        src={sitePlan.thumbnailUrl}
        alt={sitePlan.projectName}
        sx={{
          ...frameSx,
          objectFit: 'cover',
          bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
          display: 'block',
        }}
      />
    );
  }

  if (hasMapCoordinates(sitePlan.mapLatitude, sitePlan.mapLongitude)) {
    return (
      <Box sx={{ ...frameSx, overflow: 'hidden', bgcolor: 'rgba(var(--color-text-rgb), 0.05)' }}>
        <ProjectLandLotMap
          latitude={Number(sitePlan.mapLatitude)}
          longitude={Number(sitePlan.mapLongitude)}
          projectName={sitePlan.projectName}
          height={THUMBNAIL_HEIGHT}
          showLotLabel={false}
          interactive={false}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...frameSx,
        bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }}>
        Site Plan Not Available
      </Typography>
    </Box>
  );
};

export default SitePlanCardThumbnail;
